import { cardById } from "@/data/cards";
import { applyCardEffects } from "@/systems/events";
import type { TickEvent } from "@/systems/engine";
/**
 * Player/automator actions (v0.2 RFC §3–§6). Contract per ADR-002:
 * invalid-but-plausible params throw NAMING THE FIELD; illegal transitions
 * throw with the reason; legal-empty situations are EVENTS. All mutations go
 * through here or the engine.
 */

import { EVENTS,
  ALUMNI,
  TEND_BOOST,
  AUTOMATOR_ACTIONS_BY_LEVEL,
  AUTOMATOR_COST_GROWTH,
  CARE_TIERS,
  DISCHARGE,
  ECONOMY,
  METERS,
  OFFICE,
  TIERS,
  upgradeCost,
  type CareTierId,
  type DischargePolicy,
} from "@/data/tunables";
import { fireBrokerCooldown } from "@/machines/cooldown";
import { contrabandOutcome, fireCardAnswer, fireLootCollect } from "@/machines/events";
import { LADDER, fireLadderUpgrade, type LadderInstance } from "@/machines/ladder";
import { fireTierPurchase } from "@/machines/expansion";
import { applyHeatGain } from "@/systems/meters";
import {
  effectiveCapacity,
  openCareTiers,
  patientInSlot,
  patientsInTier,
  type GameState,
  type Patient,
  type PatientState,
  type TierId,
} from "@/state/GameState";
import { drawName } from "@/systems/names";

export type ActionEvent =
  | { type: "leadJoined"; leadId: number; name: string }
  | { type: "leadBalked"; name: string }
  | { type: "admitted"; patientId: number; name: string; bed: number }
  | { type: "admitBlocked"; reason: "noBed" | "emptyQueue" }
  | { type: "upgraded"; what: "recruiter" | "coordinator" | "prFirm" | "complianceOfficer" | "fixer" | CareTierId; level: number }
  | { type: "policyChanged"; tier: CareTierId; policy: DischargePolicy }
  | { type: "tierUnlocked"; tier: TierId }
  | { type: "contactCallMade"; boostUntil: number }
  | { type: "brokered"; leadId: number; name: string; payout: number };

function requireOpenTier(state: GameState, tier: string, action: string): CareTierId {
  if (!(CARE_TIERS as readonly string[]).includes(tier)) {
    throw new Error(`${action}: tier must be one of ${CARE_TIERS.join("/")}, got "${tier}"`);
  }
  if (!state.run.availableTiers.includes(tier as CareTierId)) {
    throw new Error(`${action}: tier "${tier}" is not open yet`);
  }
  return tier as CareTierId;
}

function requirePatient(state: GameState, tier: CareTierId, bed: number, action: string): Patient {
  const cap = effectiveCapacity(state, tier);
  if (!Number.isInteger(bed) || bed < 0 || bed >= cap) {
    throw new Error(`${action}: bed must be 0..${cap - 1} for ${tier}, got ${bed}`);
  }
  const p = patientInSlot(state, tier, bed);
  if (!p) {
    throw new Error(`${action}: ${tier} bed ${bed} is empty`);
  }
  return p;
}

// ── office funnel (unchanged semantics from v0.1) ──────────────────────────

export function recruit(state: GameState): ActionEvent {
  const { run } = state;
  const name = drawName(run.rng);
  if (run.queue.length >= OFFICE.queueCap) {
    return { type: "leadBalked", name };
  }
  const lead = { id: run.nextId++, name };
  run.queue.push(lead);
  return { type: "leadJoined", leadId: lead.id, name };
}

function firstOpenSlot(state: GameState, tier: CareTierId): number {
  for (let b = 0; b < effectiveCapacity(state, tier); b++) {
    if (!patientInSlot(state, tier, b)) return b;
  }
  return -1;
}

/** M4: manual admit — front-of-line → first open Detox bed. */
export function admitFromQueue(state: GameState): ActionEvent {
  const { run } = state;
  if (!run.availableTiers.includes("detox")) {
    throw new Error("admitFromQueue: detox is not accredited yet (read your mail)");
  }
  if (run.queue.length === 0) {
    return { type: "admitBlocked", reason: "emptyQueue" };
  }
  const bed = firstOpenSlot(state, "detox");
  if (bed === -1) {
    return { type: "admitBlocked", reason: "noBed" };
  }
  const lead = run.queue.shift()!;
  const patient: Patient = {
    id: lead.id,
    name: lead.name,
    tier: "detox",
    state: "new",
    progress: 0,
    issueHistory: lead.issueHistory ?? 0, // returning customers keep their M8 memory
    relapseCount: lead.relapseCount ?? 0,
    bed,
  };
  run.patients.push(patient);
  ratchetHighWater(state, "detox");
  return { type: "admitted", patientId: patient.id, name: patient.name, bed };
}

/** The ratchet only moves forward (prestige addendum). */
export function ratchetHighWater(state: GameState, reached: TierId): void {
  const order: TierId[] = [...CARE_TIERS, "alumni"];
  const cur = state.account.highWaterLegit;
  const curIdx = cur === "none" ? -1 : order.indexOf(cur);
  const newIdx = order.indexOf(reached);
  if (newIdx > curIdx) state.account.highWaterLegit = reached;
}

// ── tier care actions (generic tend/treat, per-tier verbs are UI labels) ───

export function tendSlot(state: GameState, tier: string, bed: number): PatientState {
  const t = requireOpenTier(state, tier, "tendSlot");
  const p = requirePatient(state, t, bed, "tendSlot");
  switch (p.state) {
    case "new":
    case "paused":
      p.state = "progressing";
      break;
    case "progressing":
      p.progress = Math.min(100, p.progress + TEND_BOOST);
      break;
    case "issue":
      throw new Error(`tendSlot: patient ${p.name} (${t} bed ${bed}) has an ISSUE — use Treat`);
  }
  return p.state;
}

export function treatSlot(state: GameState, tier: string, bed: number): void {
  const t = requireOpenTier(state, tier, "treatSlot");
  const p = requirePatient(state, t, bed, "treatSlot");
  if (p.state !== "issue") {
    throw new Error(`treatSlot: patient ${p.name} (${t} bed ${bed}) is ${p.state.toUpperCase()}, not ISSUE`);
  }
  p.state = "paused";
}

// ── automators (one-character-that-upgrades, per tier) ─────────────────────

/** v0.10 M2: the Ladder machine arbitrates every leveled purchase; the
 * engine applies the spend and keeps each action's exact fail-loud
 * message shape (equivalence lock). */
function ladderPurchase(state: GameState, inst: LadderInstance, who: string, maxMsg: string): number {
  const res = fireLadderUpgrade(inst, state);
  if (!res.allowed) {
    if (res.reason === "maxed") throw new Error(`${who}: ${maxMsg}`);
    if (res.reason === "gated") throw new Error(`${who}: ${res.gateMessage}`);
    throw new Error(`${who}: needs $${res.cost}, have $${Math.floor(state.run.funding)}`);
  }
  state.run.funding -= res.cost;
  return res.newLevel;
}

export function automatorUpgradeCost(state: GameState, tier: CareTierId): number {
  return upgradeCost(TIERS[tier].automatorBaseCost, AUTOMATOR_COST_GROWTH, state.run.tiers[tier].automatorLevel);
}

export function upgradeAutomator(state: GameState, tier: string): ActionEvent {
  const t = requireOpenTier(state, tier, "upgradeAutomator");
  const max = AUTOMATOR_ACTIONS_BY_LEVEL.length - 1;
  const ts = state.run.tiers[t];
  ts.automatorLevel = ladderPurchase(state, LADDER.automator(t), "upgradeAutomator", `${t} already at max level ${max}`);
  return { type: "upgraded", what: t, level: ts.automatorLevel };
}

export function recruiterUpgradeCost(state: GameState): number {
  return upgradeCost(OFFICE.recruiterBaseCost, OFFICE.recruiterCostGrowth, state.run.office.recruiterLevel);
}

export function upgradeRecruiter(state: GameState): ActionEvent {
  const max = OFFICE.recruiterIntervalByLevel.length - 1;
  state.run.office.recruiterLevel = ladderPurchase(state, LADDER.recruiter(), "upgradeRecruiter", `already at max level ${max}`);
  return { type: "upgraded", what: "recruiter", level: state.run.office.recruiterLevel };
}

// ── discharge policy (M6) ───────────────────────────────────────────────────

export function setDischargePolicy(state: GameState, tier: string, policy: string): ActionEvent {
  const t = requireOpenTier(state, tier, "setDischargePolicy");
  if (!(policy in DISCHARGE)) {
    throw new Error(`setDischargePolicy: policy must be ${Object.keys(DISCHARGE).join("/")}, got "${policy}"`);
  }
  state.run.tiers[t].dischargePolicy = policy as DischargePolicy;
  return { type: "policyChanged", tier: t, policy: policy as DischargePolicy };
}

// ── tier expansion PURCHASE (v0.3: letter-gated; the stub is gone) ─────────

/** Buy the tier an expansion-offer letter made purchasable (three-layer §4). */
export function purchaseTier(state: GameState): ActionEvent {
  // v0.10 M1: the TierExpansion machine arbitrates BUY (purchasable→open);
  // exact fail-loud messages preserved (equivalence lock).
  const res = fireTierPurchase(state);
  if (!res.allowed) {
    if (res.reason === "noOffer") {
      throw new Error("purchaseTier: no expansion offer is open (read the letter first)");
    }
    if (res.reason === "alreadyOpen") {
      throw new Error(`purchaseTier: tier "${state.run.purchasable!.tier}" is already open`);
    }
    throw new Error(`purchaseTier: needs $${res.price}, have $${Math.floor(state.run.funding)}`);
  }
  state.run.funding -= res.price;
  state.run.availableTiers.push(res.tier);
  ratchetHighWater(state, res.tier);
  state.run.purchasable = null;
  return { type: "tierUnlocked", tier: res.tier };
}

// ── alumni actions (M11) ────────────────────────────────────────────────────

export function setFacilityName(state: GameState, raw: string): void {
  const clean = raw.replace(/[<>]/g, "").replace(/[\u0000-\u001f]/g, "").trim().slice(0, 40);
  state.account.facilityName = clean.length > 0 ? clean : "Serenity Halfway House";
}

export function contactCall(state: GameState): ActionEvent {
  if (!state.run.availableTiers.includes("alumni")) {
    throw new Error("contactCall: the Alumni Network is not open yet");
  }
  state.run.alumni.contactBoostUntil = state.run.tick + ALUMNI.contactCallBoostTicks;
  return { type: "contactCallMade", boostUntil: state.run.alumni.contactBoostUntil };
}

export function coordinatorUpgradeCost(state: GameState): number {
  return upgradeCost(ALUMNI.coordinatorBaseCost, AUTOMATOR_COST_GROWTH, state.run.alumni.coordinatorLevel);
}

export function upgradeCoordinator(state: GameState): ActionEvent {
  const max = AUTOMATOR_ACTIONS_BY_LEVEL.length - 1;
  state.run.alumni.coordinatorLevel = ladderPurchase(state, LADDER.coordinator(), "upgradeCoordinator", `already at max level ${max}`);
  return { type: "upgraded", what: "coordinator", level: state.run.alumni.coordinatorLevel };
}

// ── v0.5 body broker + mitigation staff ─────────────────────────────────────

/** Broker one lead (M3, RULED): unlocked + FULL line only; sells the FRONT.
 * v0.10 Control Room: the cooldown MACHINE is the arbiter — 45 ticks
 * between sales (the #40 exploit patch; the minor's one intended change). */
/** v0.13: grab an active loot drop (RFC §3.2). Money pays funding;
 * contraband pays through the other business AND heats you up (RULED
 * §8.3b, one arbiter + one economics fn — machines/events.ts). */
export function collectLoot(state: GameState, lootId: number): TickEvent {
  const fire = fireLootCollect(state, lootId);
  if (!fire.allowed) {
    throw new Error(`collectLoot: no active loot with id ${lootId} — grabbed already, or it despawned`);
  }
  let funding = 0;
  let heat = 0;
  let sk = 0;
  if (fire.kind === "money") {
    funding = fire.value;
  } else {
    const o = contrabandOutcome(state, fire.value);
    funding = o.funding;
    heat = o.heat;
    sk = o.sk;
  }
  state.run.funding += funding;
  if (heat > 0) applyHeatGain(state, heat); // canonical path — ratchets/thresholds ride along (v0.13 review)
  state.account.streetKnowledge += sk;
  state.run.events.active = state.run.events.active.filter((l) => l.id !== lootId);
  state.run.events.cooldownLeft[`loot:${fire.tier}`] = EVENTS.loot.cooldownTicks; // resolve-anchored (M5 chart)
  return { type: "lootCollected", id: lootId, kind: fire.kind, tier: fire.tier, item: fire.item, funding, heat, sk };
}

/** v0.14: answer an open event card. Applies the chosen branch, clears
 * the card, opens the scene's cooldown. Fail-loud on unknown card / bad
 * choice index (the arbiter's refusal). The frozen scene resumes next
 * tick once the card is gone. */
export function answerCard(state: GameState, cardId: number, choiceIndex: number): TickEvent {
  const fire = fireCardAnswer(state, cardId, choiceIndex);
  if (!fire.allowed) {
    throw new Error(`answerCard: ${fire.reason === "unknownId" ? `no open card with id ${cardId}` : `choice ${choiceIndex} out of range for card ${cardId}`}`);
  }
  const def = cardById(fire.defId);
  applyCardEffects(state, def.choices[choiceIndex]!.effects, def.scene);
  state.run.events.cards = state.run.events.cards.filter((c) => c.id !== cardId);
  state.run.events.cooldownLeft[`card:${def.scene}`] = EVENTS.card.cooldownTicks;
  return { type: "cardResolved", id: cardId, defId: fire.defId, scene: def.scene, expired: false };
}

export function brokerLead(state: GameState): ActionEvent {
  if (!state.run.brokerUnlocked) {
    throw new Error("brokerLead: the broker is not unlocked yet (read the invitation)");
  }
  if (state.run.queue.length < OFFICE.queueCap) {
    throw new Error(
      `brokerLead: the line must be FULL to broker — need ${OFFICE.queueCap}, have ${state.run.queue.length}`,
    );
  }
  const cooldown = fireBrokerCooldown(state);
  if (!cooldown.allowed) {
    throw new Error(`brokerLead: broker cooling down — ${cooldown.ticksLeft} tick(s) left`);
  }
  state.run.brokerLockoutUntil = cooldown.lockUntil;
  const lead = state.run.queue.shift()!; // front of the line (RULED §11.1)
  const { run } = state;
  run.funding += METERS.brokerPayout;
  // Broker cash is skill income — it MUST feed NET/SEC + peak (RFC §6), so log
  // it like a completion payout. The engine's logIncome only runs on care-tier
  // completions, so trim the window here too (leg-4: Codex).
  run.incomeLog.push({ tick: run.tick, amount: METERS.brokerPayout });
  const cutoff = run.tick - ECONOMY.netWindowTicks;
  while (run.incomeLog.length > 0 && run.incomeLog[0]!.tick <= cutoff) run.incomeLog.shift();
  applyHeatGain(state, METERS.brokerHeat);
  run.brokerHistory += 1;
  return { type: "brokered", leadId: lead.id, name: lead.name, payout: METERS.brokerPayout };
}

export function prFirmUpgradeCost(state: GameState): number {
  return upgradeCost(METERS.prFirmBaseCost, METERS.mitigationCostGrowth, state.run.prFirmLevel);
}

export function upgradePrFirm(state: GameState): ActionEvent {
  state.run.prFirmLevel = ladderPurchase(state, LADDER.prFirm(), "upgradePrFirm", `already at max level ${METERS.mitigationMaxLevel}`);
  return { type: "upgraded", what: "prFirm", level: state.run.prFirmLevel };
}

export function complianceUpgradeCost(state: GameState): number {
  return upgradeCost(METERS.complianceBaseCost, METERS.mitigationCostGrowth, state.run.complianceOfficerLevel);
}

export function upgradeComplianceOfficer(state: GameState): ActionEvent {
  state.run.complianceOfficerLevel = ladderPurchase(state, LADDER.compliance(), "upgradeComplianceOfficer", `already at max level ${METERS.mitigationMaxLevel}`);
  return { type: "upgraded", what: "complianceOfficer", level: state.run.complianceOfficerLevel };
}

export function fixerUpgradeCost(state: GameState): number {
  return upgradeCost(METERS.fixerBaseCost, METERS.mitigationCostGrowth, state.run.fixerLevel);
}

export function upgradeFixer(state: GameState): ActionEvent {
  state.run.fixerLevel = ladderPurchase(state, LADDER.fixer(), "upgradeFixer", `already at max level ${METERS.mitigationMaxLevel}`);
  return { type: "upgraded", what: "fixer", level: state.run.fixerLevel };
}

// ── v0.1 compat re-exports for the office/detox call sites + tests ─────────

export { patientInSlot, patientsInTier, openCareTiers };
